import React, { useState } from 'react';
import _ from 'lodash';
import styles from './NavMenu.module.scss';
import classnames from 'classnames';
import { Col, Row } from 'antd';
import { GlobalImage } from 'src/modules/global';
import { rowThumbnailProps } from 'src/utils/grid';
import { NavMenu, NavMenuProps } from 'src/modules/layout/components/header/components/NavMenu';
import Link from 'next/link';

type Subcategory = { id: string; name: string; slug: string };

export interface NavMainMenuLink {
  url: string;
  text: string;
  subcategories?: Subcategory[];
  linkType?: 'link' | 'divider';
}

export interface NavMainMenuImage {
  imageUrl: string;
  caption: string;
  linkUrl: string;
}

export interface NavMainMenuProps extends Omit<NavMenuProps, 'children'> {
  links: NavMainMenuLink[];
  title: string;
  images?: NavMainMenuImage[];
  moreLink?: NavMainMenuLink;
  objectFit?: 'contain' | 'cover';
}

const filterOutSubcategories = (link: NavMainMenuLink) => {
  const REMOVE_SLUGS = {
    // 1
    '/products/chairs-barstools/lounge-chairs': true,
    // 2
    '/products/lounge-chairs/executive-conference-chairs': true,
    '/products/lounge-chairs/dining-guest-chairs': true,
    // 3
    '/products/sofas-benches/bed-accessoires': true,
    '/products/sofas-benches/sofa-pouf': true,
    // 4
    '/products/tables/accessories-stools': true,
    '/products/tables/bed-accessoires': true,
    // 5
    '/products/carpets-carpet-pillows/carpets': true,
    // 6
    '/products/accessories-stools/coffee-side-tables': true,
    '/products/accessories-stools/side-tables': true,
    '/products/accessories-stools/bed': true,
    // 7
    '/products/beds/side-tables': true,
    '/products/beds/accessories-stools': true,
  };

  const filteredSubcategories = _.filter(link.subcategories, (subcategory) => {
    const key = `${link.url}/${subcategory.slug}`;

    return !REMOVE_SLUGS[key];
  });

  return { ...link, subcategories: filteredSubcategories };
};

export const NavMainMenu = ({
  name,
  openMenu,
  closeMenu,
  activeMenu,
  links,
  title,
  moreLink,
  objectFit = 'cover',
  images: allImages = [],
}: NavMainMenuProps) => {
  const images = allImages.slice(0, 3);
  const imageColSize = 24 / images.length;
  // const { getLocalizedObject } = useLocale();
  const [filterApplied, setFilterApplied] = useState(-1);
  const [subcategories, setSubcategories] = useState<Subcategory[] | null>(null);
  const [selectedLink, setSelectedLink] = useState(null);
  const [subcategoriesClassName, setSubcategoriesClassName] = useState(
    classnames(styles.subcategories, styles.subcategoriesHidden)
  );
  const [leftDrawerClassName, setLeftDrawerClassName] = useState(styles.leftDrawer);

  const onMouseLeave = () => {
    setFilterApplied(-1);
    setSubcategories(null);
    setSelectedLink(null);
    setSubcategoriesClassName(classnames(styles.subcategories, styles.subcategoriesHidden));
    setLeftDrawerClassName(styles.leftDrawer);
  };

  const onMouseEnter = (link: any, i: number) => {
    if (i === filterApplied) return;
    setFilterApplied(i);

    const hoverLink = _.find(links, (l) => {
      if (!l?.subcategories) return false;

      return l.url === link.url;
    });

    if (!_.isEmpty(hoverLink?.subcategories)) {
      setSelectedLink(hoverLink);

      const filteredHoverLink = filterOutSubcategories(hoverLink);
      const sortedSubcategories = _.sortBy(filteredHoverLink.subcategories, (s) => s?.name);

      setSubcategories(sortedSubcategories);
      setSubcategoriesClassName(styles.subcategories);
      setLeftDrawerClassName(classnames(styles.leftDrawer, styles.leftDrawerSubcategories));
    } else {
      onMouseLeave();
    }
  };

  return (
    <div onMouseLeave={onMouseLeave}>
      <NavMenu openMenu={openMenu} closeMenu={closeMenu} activeMenu={activeMenu} name={name}>
        <Row>
          <Col span={6} className={leftDrawerClassName}>
            <div onMouseEnter={onMouseLeave} className={styles.menuTitle}>
              {title}
            </div>
            {links.map((link, i) => {
              if (link?.linkType === 'divider') {
                return <div className={styles.linkDivider} />;
              }

              const linkClassName =
                selectedLink?.url === link?.url
                  ? classnames(styles.menuLink, styles.menuLinkSelected)
                  : styles.menuLink;

              return (
                <div key={i} onMouseEnter={() => onMouseEnter(link, i)}>
                  <Link href={link.url}>
                    <a className={linkClassName}>{link.text}</a>
                  </Link>
                </div>
              );
            })}
            {moreLink && (
              <a href={moreLink.url} className={classnames(styles.menuLink, styles.moreLink)}>
                {moreLink.text}
              </a>
            )}
          </Col>
          <Col span={18}>
            <div className={styles.imagesRowContainer}>
              <Row {...rowThumbnailProps}>
                {images.map((image, i) => {
                  return (
                    <Col key={i} span={imageColSize}>
                      <Link href={image.linkUrl}>
                        <a>
                          <GlobalImage
                            className={styles.image}
                            ratio={1.5}
                            url={image.imageUrl}
                            caption={image.caption}
                            captionSize="medium"
                            objectFit={objectFit}
                          />
                        </a>
                      </Link>
                    </Col>
                  );
                })}
              </Row>
            </div>
            <Row>
              <div className={subcategoriesClassName}>
                {_.map(subcategories, (subcategory, i) => {
                  const href = `${selectedLink.url}?productSubcategories=${subcategory.slug}`;
                  return (
                    <Link href={href} as={href} key={i}>
                      <a className={classnames(styles.menuLink, styles.subcategoryLink)}>
                        {subcategory.name}
                      </a>
                    </Link>
                  );
                })}
              </div>
            </Row>
          </Col>
        </Row>
      </NavMenu>
    </div>
  );
};
